import type { AxiosInstance } from "axios";

import { validateResponse } from "api/validateResponse";

import type { GetClinicPartnerPatientsArgs } from "./schemas/dashboard";
import {
  type GetClinicPartnerActivePatientsArgs,
  GetClinicPartnerActivePatientsResponseSchema,
  GetClinicPartnerAvgFunctionalityResponseSchema,
  GetClinicPartnerAvgNpsResponseSchema,
  GetClinicPartnerAvgPainResponseSchema,
  type GetClinicPartnerEnrolledPatientsArgs,
  GetClinicPartnerEnrolledPatientsResponseSchema,
  GetClinicPartnerPatientsResponseSchema,
} from "./schemas/dashboard";
import type { GetClinicPartnerInvitationsArgs } from "./schemas/invitations";
import {
  GetClinicPartnerInvitationsResponseSchema,
  GetUsProviderGroupClinicsResponseSchema,
  type PostClinicPartnerInvitationArgs,
  PostClinicPartnerInvitationResponseSchema,
} from "./schemas/invitations";
import { GetClinicPartnersMembersResponseSchema } from "./schemas/members";

interface CreateClinicPartnerServiceArgs {
  transportV2: AxiosInstance;
}

export const createClinicPartnerService = ({ transportV2 }: CreateClinicPartnerServiceArgs) => ({
  async getClinicPartnerActivePatients(args: GetClinicPartnerActivePatientsArgs) {
    const response = await transportV2.get("/clinic_partners/active_patients", {
      params: args.params,
    });
    return validateResponse(response, GetClinicPartnerActivePatientsResponseSchema, false);
  },

  async getClinicPartnerAvgFunctionality() {
    const response = await transportV2.get("/clinic_partners/avg_functionality");
    return validateResponse(response, GetClinicPartnerAvgFunctionalityResponseSchema, false);
  },

  async getClinicPartnerAvgNps() {
    const response = await transportV2.get("/clinic_partners/avg_nps");
    return validateResponse(response, GetClinicPartnerAvgNpsResponseSchema, false);
  },

  async getClinicPartnerAvgPain() {
    const response = await transportV2.get("/clinic_partners/avg_pain");
    return validateResponse(response, GetClinicPartnerAvgPainResponseSchema, false);
  },

  async getClinicPartnerEnrolledPatients(args: GetClinicPartnerEnrolledPatientsArgs) {
    const response = await transportV2.get("/clinic_partners/enrolled_patients", {
      params: args.params,
    });
    return validateResponse(response, GetClinicPartnerEnrolledPatientsResponseSchema, false);
  },

  async getClinicPartnerInvitations(args: GetClinicPartnerInvitationsArgs) {
    const response = await transportV2.get("/clinic_partners/invitations", {
      params: args.params,
    });
    return validateResponse(response, GetClinicPartnerInvitationsResponseSchema, false);
  },

  async getClinicPartnerPatients(args: GetClinicPartnerPatientsArgs) {
    const response = await transportV2.get("/clinic_partners/patients", {
      params: args.params,
    });
    return validateResponse(response, GetClinicPartnerPatientsResponseSchema, false);
  },

  async getClinicPartnersMembers() {
    const response = await transportV2.get("/clinic_partners/members");
    return validateResponse(response, GetClinicPartnersMembersResponseSchema, false);
  },

  async getUsProviderGroupClinics() {
    const response = await transportV2.get("/clinic_partners/us_provider_group_clinics");
    return validateResponse(response, GetUsProviderGroupClinicsResponseSchema, false);
  },

  async postClinicPartnerInvitation(args: PostClinicPartnerInvitationArgs) {
    const response = await transportV2.post("/clinic_partners/invitations", args.payload);
    return validateResponse(response, PostClinicPartnerInvitationResponseSchema, false);
  },

  async postResendClinicPartnerInvitation(id: number) {
    const response = await transportV2.post(`/clinic_partners/invitations/${id}/resend`);
    return validateResponse(response, PostClinicPartnerInvitationResponseSchema, false);
  },
});

export type ClinicPartnerService = ReturnType<typeof createClinicPartnerService>;
